import { createFileRoute, Link } from '@tanstack/react-router'
import { pageSeo } from '@/lib/seo'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { buttonVariants } from '@/components/ui/button'
import { AuthCard } from '@/routes/_app/-components/shared/auth-card'
import { AuthPending } from '@/routes/_app/-components/app-layout-skeleton'
import { Spinner } from '@/components/ui/spinner'
import { trpc } from '@/main'

export const Route = createFileRoute('/_public/verify-email/$token')({
  head: (ctx) =>
    pageSeo(ctx, {
      title: msg`Verify your email`,
      description: msg`Confirm your email address to finish setting up your D’Anchiano account.`,
      noindex: true,
    }),
  component: RouteComponent,
  pendingComponent: AuthPending,
})

function RouteComponent() {
  const { token } = Route.useParams()
  const { t } = useLingui()
  const [result, setResult] = useState<{
    status: 'idle' | 'pending' | 'success' | 'error'
    companyName?: string | null
    email?: string
  }>({ status: 'idle' })

  const verifyEmail = useMutation(trpc.auth.verifyEmail.mutationOptions())
  const confirmEmailChange = useMutation(trpc.auth.confirmEmailChange.mutationOptions())
  const calledRef = useRef(false)

  useEffect(() => {
    if (calledRef.current) return
    calledRef.current = true

    setResult({ status: 'pending' })
    // Try confirmEmailChange first (for personal email changes), then fall back to verifyEmail (corporate)
    confirmEmailChange.mutateAsync({ token }).then(
      (data) => setResult({ status: 'success', email: data.email }),
      () =>
        verifyEmail.mutateAsync({ token }).then(
          (data) => setResult({ status: 'success', companyName: data.companyName }),
          () => setResult({ status: 'error' }),
        ),
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Lingui messages need plain variables, not member expressions.
  const companyName = result.companyName
  const email = result.email
  const isPending = result.status === 'pending' || result.status === 'idle'

  const title = isPending
    ? t`Verifying your email…`
    : result.status === 'success'
      ? t`Your email has been verified`
      : t`We couldn't verify this email`

  const subtitle = isPending
    ? undefined
    : result.status === 'success'
      ? companyName
        ? t`${companyName} added you as an employee, and your psychographic profile is shared with them. Sign in to see your profile, exactly as they see it.`
        : email
          ? t`Your email has been updated to ${email}. You can now sign in with your new address.`
          : t`You can now sign in with this email address.`
      : t`The link may have expired or already been used.`

  return (
    <AuthCard home title={title} subtitle={subtitle} contentClassName="flex flex-col gap-5">
      {isPending ? (
        <div className="flex justify-center">
          <Spinner className="text-muted-foreground size-6" />
        </div>
      ) : (
        <Link to="/login" className={buttonVariants({ className: 'w-full' })}>
          {t`Go to sign in`}
        </Link>
      )}
    </AuthCard>
  )
}
